import React, { useEffect, useRef, useState } from 'react';
import { Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { CATEGORY_COLOR, CATEGORY_LABEL } from '../../constants';
import { formatPublishNp } from '../../utils/timeAgo';

function Hero({ items = [], side = [], onOpen }) {
  const [index, setIndex] = useState(0);
  const touchX = useRef(null);
  const slides = items.slice(0, 5);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(t);
  }, [index, slides.length]);

  if (!slides.length) return null;

  const safeIndex = index % slides.length;
  const main = slides[safeIndex];

  const go = (dir) => setIndex((i) => (i + dir + slides.length) % slides.length);

  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx > 40) go(-1);
    else if (dx < -40) go(1);
    touchX.current = null;
  };

  const catColor = CATEGORY_COLOR[main.category_slug] || 'var(--crimson)';
  const catLabel = CATEGORY_LABEL[main.category_slug] || 'समाचार';

  return (
    <section className="grid gap-6 lg:grid-cols-[1.7fr_1fr] items-stretch mb-10">
      <article
        className="relative rounded-[16px] overflow-hidden cursor-pointer min-h-[340px] sm:min-h-[430px] group select-none"
        onClick={() => onOpen(main)}
        role="button"
        tabIndex={0}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img
          key={main.id}
          src={main.image || 'https://via.placeholder.com/1200x800?text=Kalo+Jhola'}
          alt={main.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
        <div className="absolute left-0 right-0 bottom-0 p-5 sm:p-7 pr-24">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full text-white" style={{ background: catColor }}>
            <span className="w-1.5 h-1.5 rounded-full bg-white" /> {catLabel}
          </span>
          <h1 className="font-display text-white text-[clamp(20px,3vw,30px)] font-bold leading-tight mt-3 transition-colors group-hover:underline">
            {main.title}
          </h1>
          <div className="flex items-center gap-2 text-white/80 text-[12.5px] mt-3 flex-wrap">
            <Clock size={13} />
            <span>{formatPublishNp(main.created_at || main.date)}</span>
            {main.read_time && (
              <>
                <span className="opacity-60">•</span>
                <span>{main.read_time} मिनेट पढाइ</span>
              </>
            )}
          </div>
        </div>

        {slides.length > 1 && (
          <>
            <button
              className="absolute top-1/2 -translate-y-1/2 left-3 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center backdrop-blur-sm hover:bg-crimson transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                go(-1);
              }}
              aria-label="Previous"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              className="absolute top-1/2 -translate-y-1/2 right-3 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center backdrop-blur-sm hover:bg-crimson transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                go(1);
              }}
              aria-label="Next"
            >
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-4 right-4 flex items-center gap-1.5">
              {slides.map((s, i) => (
                <button
                  key={s.id}
                  className={`h-2 rounded-full transition-all ${i === safeIndex ? 'w-6 bg-white' : 'w-2 bg-white/50 hover:bg-white/80'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIndex(i);
                  }}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </article>

      <div className="flex flex-col gap-2 min-w-0 bg-white border border-line rounded-[16px] p-3 sm:p-4 shadow-sm">
        {side.slice(0, 5).map((item, idx) => (
          <article
            key={item.id}
            className="group relative flex items-center gap-3 cursor-pointer p-1.5 rounded-[10px] hover:bg-paper-soft transition-colors"
            onClick={() => onOpen(item)}
            role="button"
            tabIndex={0}
          >
            <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-[11px] font-bold text-muted group-hover:text-crimson transition-colors">
              {idx + 1}
            </span>
            <div className="w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0 rounded-[10px] overflow-hidden bg-paper-tint">
              <img
                src={item.image || 'https://via.placeholder.com/120x120?text=Kalo+Jhola'}
                alt={item.title}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-[12.5px] sm:text-[13px] font-semibold leading-snug line-clamp-2 transition-colors group-hover:text-blue">
                {item.title}
              </h3>
              <span className="flex items-center gap-1 text-[11px] text-muted mt-0.5">
                <Clock size={10} /> {formatPublishNp(item.created_at || item.date)}
              </span>
            </div>
            <span className="absolute right-2 text-[11px] text-muted opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronRight size={14} />
            </span>
          </article>
        ))}
        {side.length === 0 && <p className="text-[12.5px] text-muted p-2 text-center">थप समाचार उपलब्ध छैन।</p>}
      </div>
    </section>
  );
}

export default Hero;